import type { UseQueryOptions } from "@tanstack/react-query";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";

import type { CommunityFeedItemV2Dto, MessageDetailV2Dto, MessageV2Dto, PollDto } from "@/api/types";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useCommunityPostViewCountTrackingManager } from "@/providers/CommunityPostViewCountTrackingProvider";
import { communityFeedMutations } from "@/queries/community-feed/mutations";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { QUERY_KEYS } from "@/query-keys";
import type { ApiQueryParams } from "@/types/api-types";

import type { CommunityFeedAudienceItem } from "./types";
import { CommunityPostStatus } from "./types";

export const MAX_LENGTH_RESIDENT_POST_TITLE = 255;
export const MAX_LENGTH_RESIDENT_POST_CONTENT = 10000;
// This is the date when reach started to be computed per post
export const MIN_COMMUNITY_POST_REACH_DETAILS_DATE = new Date(2025, 8, 25);
export const ALL_COMMUNITY_FEED_ITEM_TYPES = [
  "messages",
  "polls",
  "surveys",
] satisfies ApiQueryParams<"getCommunityFeedV2">["FeedTypes"];

const VIEW_TIME_THRESHOLD = 2000; // 2 seconds

// Responsible for pushing community feed item to the global tracking queue if applicable
export const useTrackCommunityPostViewCount = ({
  itemId,
  type,
  isEnabled,
}: {
  itemId: string;
  type: CommunityFeedItemV2Dto["type"];
  isEnabled?: boolean;
}): {
  onStartTracking: () => void;
  onCancelTracking: () => void;
} => {
  const timer = useRef<ReturnType<typeof setTimeout>>(null);
  const { addToQueue: addToTrackingQueue } = useCommunityPostViewCountTrackingManager();

  const onStartTracking = useCallback(() => {
    if (!isEnabled) return;

    timer.current = setTimeout(() => {
      addToTrackingQueue({ itemId, type });
    }, VIEW_TIME_THRESHOLD);
  }, [addToTrackingQueue, isEnabled, itemId, type]);

  const onCancelTracking = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
  }, []);

  return {
    onStartTracking,
    onCancelTracking,
  };
};

/**
 * Derives the visible post status from its lifecycle timestamps.
 * Deleted takes precedence, then an active schedule, then archived; otherwise the post is delivered.
 */
export const getCommunityMessageStatus = (
  message: Pick<MessageV2Dto, "postedAt" | "scheduledAt" | "deletedAt" | "archivedAt">,
): CommunityPostStatus => {
  if (message.postedAt && !message.scheduledAt && !message.deletedAt && !message.archivedAt) {
    return CommunityPostStatus.DELIVERED;
  } else if (message.scheduledAt && !message.deletedAt) {
    return CommunityPostStatus.SCHEDULED;
  } else if (message.deletedAt) {
    return CommunityPostStatus.DELETED;
  } else if (message.archivedAt) {
    return CommunityPostStatus.ARCHIVED;
  } else {
    return CommunityPostStatus.DELIVERED;
  }
};

export const getCommunityPollStatus = (poll: PollDto): CommunityPostStatus => {
  if (poll.postedAt && !poll.scheduledAt && !poll.deletedAt) {
    return CommunityPostStatus.DELIVERED;
  } else if (poll.scheduledAt && !poll.deletedAt) {
    return CommunityPostStatus.SCHEDULED;
  } else if (poll.deletedAt) {
    return CommunityPostStatus.DELETED;
  } else {
    return CommunityPostStatus.DELIVERED;
  }
};

// oxlint-disable-next-line typescript/explicit-module-boundary-types
export const useData = (message: MessageV2Dto) => {
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.setQueryData(QUERY_KEYS.MESSAGE_DETAILS(projectId, message.id), message);
  }, [queryClient, message, projectId]);

  const communityFeedQueries = useCommunityFeedQueries();
  const { data: messageDetails } = useQuery({
    ...(communityFeedQueries.messageDetails(message.id) as UseQueryOptions<MessageV2Dto & Partial<MessageDetailV2Dto>>),
    initialData: message,
  });

  const createQuickReplyLink = communityFeedMutations.useGetQuickReplyLink();
  const deletePost = communityFeedMutations.useDeleteMessage();
  const archivePost = communityFeedMutations.useArchiveMessage();
  const markAsRead = communityFeedMutations.useMarkMessageAsRead(message.id);

  return {
    post: messageDetails,
    isMarkingAsRead: markAsRead.isPending,
    isDeleting: deletePost.isPending,
    markAsRead: markAsRead.mutateAsync,
    deletePost: deletePost.mutateAsync,
    archivePost: archivePost.mutateAsync,
    copyQuickReply: createQuickReplyLink.mutateAsync,
  };
};

export const convertToCommunityFeedAudienceItemType = <T extends CommunityFeedAudienceItem["type"]>(
  type: T,
): ((value: CommunityFeedAudienceItem["value"]) => CommunityFeedAudienceItem) => {
  return (value) =>
    ({
      type,
      value,
    }) as CommunityFeedAudienceItem;
};
